// ── CulinexOS · Notify Chime + Browser Notifications ─────────────────────────
//
// Lightweight foreground-first alerting layer:
//   • fireChime() — plays a soft two-note ping via Web Audio when new activity
//     hits any notification bucket. Throttled to 1× / 3s.
//   • fireNotification() — shows an OS-level Notification (using the standard
//     Notification API) ONLY when the tab is not visible; foreground tabs get
//     the bubble-pulse + chime treatment instead so we don't stack duplicates.
//   • requestPushPermission() — walks the user through the permission prompt.
//   • isPushEnabled() / setPushEnabled() — persistent opt-in flag.
//
// Real push-when-closed would need a VAPID push server + service-worker
// subscription; keeping this foreground-only avoids a heavy 3rd-party
// integration while still delivering the "sidebar bubbles chime" experience.

export const NOTIFY_KEY = "cx_push_enabled";
export const ALERT_PREFS_KEY = "cx_alert_prefs";
export const SOUND_PACK_KEY = "cx_chime_pack";

// Three curated chime packs — pick the one that matches your kitchen's vibe.
export const SOUND_PACKS = [
  { key: "chime", label: "Chime", notes: [{ f: 880, at: 0 }, { f: 1320, at: 0.14 }] },
  { key: "bell",  label: "Bell",  notes: [{ f: 1046.5, at: 0, dur: 0.34 }] },
  { key: "xylo",  label: "Xylo",  notes: [{ f: 587, at: 0 }, { f: 784, at: 0.09 }, { f: 988, at: 0.18 }] },
];
export function getSoundPack() {
  try { return localStorage.getItem(SOUND_PACK_KEY) || "chime"; } catch { return "chime"; }
}
export function setSoundPack(key) {
  try { localStorage.setItem(SOUND_PACK_KEY, key); } catch { /* ignore */ }
}

// Per-bucket alert preferences — owner can silence noisy buckets (e.g. mute waste,
// keep orders). Defaults: high-signal ops on, back-office chatter off.
export const ALERT_BUCKETS = [
  { key: "orders",    label: "New orders" },
  { key: "kds",       label: "Kitchen tickets" },
  { key: "approvals", label: "Approvals pending" },
  { key: "delivery",  label: "Delivery board" },
  { key: "inventory", label: "Low-stock ingredients" },
  { key: "waste",     label: "Waste alerts" },
  { key: "expenses",  label: "Expense flags" },
];
export const DEFAULT_ALERT_PREFS = {
  orders: true, kds: true, approvals: true, delivery: true,
  inventory: true, waste: false, expenses: false,
};

export function getAlertPrefs() {
  try {
    const raw = localStorage.getItem(ALERT_PREFS_KEY);
    if (!raw) return { ...DEFAULT_ALERT_PREFS };
    return { ...DEFAULT_ALERT_PREFS, ...JSON.parse(raw) };
  } catch { return { ...DEFAULT_ALERT_PREFS }; }
}
export function setAlertPrefs(next) {
  try { localStorage.setItem(ALERT_PREFS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  try { window.dispatchEvent(new CustomEvent("alert-prefs:change", { detail: next })); } catch { /* ignore */ }
}
export function isBucketEnabled(key) {
  return getAlertPrefs()[key] !== false;
}

export const isPushEnabled = () => {
  try { return localStorage.getItem(NOTIFY_KEY) === "1"; }
  catch { return false; }
};

export const setPushEnabled = (v) => {
  try { localStorage.setItem(NOTIFY_KEY, v ? "1" : "0"); }
  catch { /* ignore */ }
};

export async function requestPushPermission() {
  if (!("Notification" in window)) return "unsupported";
  if (Notification.permission === "granted") { setPushEnabled(true); return "granted"; }
  if (Notification.permission === "denied") { setPushEnabled(false); return "denied"; }
  const p = await Notification.requestPermission();
  setPushEnabled(p === "granted");
  return p;
}

let lastChimeAt = 0;
export function fireChime(previewPack) {
  // Preview mode (pack key passed explicitly) bypasses the enabled flag and throttle,
  // so the owner can audition packs from the preferences panel.
  if (!previewPack) {
    if (!isPushEnabled()) return;
    const now = Date.now();
    if (now - lastChimeAt < 3000) return;
    lastChimeAt = now;
  }
  const pack = SOUND_PACKS.find((p) => p.key === (previewPack || getSoundPack())) || SOUND_PACKS[0];
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const play = (freq, delay, dur = 0.16) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, ctx.currentTime + delay);
      gain.gain.linearRampToValueAtTime(0.15, ctx.currentTime + delay + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + delay);
      osc.stop(ctx.currentTime + delay + dur + 0.05);
    };
    pack.notes.forEach((n) => play(n.f, n.at, n.dur || 0.16));
    setTimeout(() => ctx.close(), 900);
  } catch { /* ignore audio issues */ }
}

const KEY_LABELS = {
  orders:    (n) => `${n} new order${n > 1 ? "s" : ""}`,
  kds:       (n) => `${n} kitchen ticket${n > 1 ? "s" : ""} waiting`,
  approvals: (n) => `${n} approval${n > 1 ? "s" : ""} pending`,
  delivery:  (n) => `${n} delivery ${n > 1 ? "items" : "item"} to dispatch`,
  waste:     (n) => `${n} waste alert${n > 1 ? "s" : ""}`,
  inventory: (n) => `${n} low-stock ingredient${n > 1 ? "s" : ""}`,
  expenses:  (n) => `${n} expense${n > 1 ? "s" : ""} pending`,
};

export function fireNotification(risenKeys, flat) {
  if (!isPushEnabled()) return;
  if (!("Notification" in window) || Notification.permission !== "granted") return;
  // When tab is visible the bubble pulse + chime already communicates —
  // showing an OS notification on top would be double-noise.
  if (document.visibilityState === "visible") return;
  const lines = [...risenKeys].map((k) =>
    KEY_LABELS[k] ? KEY_LABELS[k](flat[k]) : `${flat[k]} ${k}`
  );
  try {
    new Notification("Counter Culture · Fresh activity", {
      body: lines.join(" · "),
      icon: "/brand/counter-culture/icon-512.png",
      tag: "cx-badge-alert",
      renotify: true,
    });
  } catch { /* ignore */ }
}
